import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import Select from "react-select";
import { useAppDispatch } from "src/redux/storeHooks";
import { getPlatformParentFieldDataCategoriesAsync } from "src/redux/actions/templateAndPricingActions";
import { CATEGORY_ATTRIBUTE_FOR_SEARCH } from "src/interfaces/Template.interface";

type OptionType = { value: number; label: string };

interface Category {
  id: number
  api_name: string
  api_id: string
  api_code: string
  updated_at: string
  created_at: string
  parent_data: number
  platform: number
  platform_field: number
}

type CategoryAttribute = {
  val: any
  id: number
  category: Category
  api_id: string
  api_code: string
  api_name: string
  display_name: string
  isRequired: boolean
  input_type: string
  description: string
}

type CategoryAttributeForSelectPropsType = {
  val: any;
  platformid: number;
  attribute: any;
  handleValueChange: (attributeId: number, value: number | null) => void;
  selectedValues: { [key: number]: number | null };
};

const PAGE_SIZE = 30;

const toOptionalNumericId = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

const CategoryAttributeForSelect: FC<CategoryAttributeForSelectPropsType> = ({
  val,
  platformid,
  attribute,
  handleValueChange,
  selectedValues,
}) => {
  const dispatch = useAppDispatch();

  const [options, setOptions] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextPage, setNextPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [search, setSearch] = useState("");

  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const isFetching = useRef(false);

  const fetchData = async (page: number, query: string = "") => {
    const res: any = await dispatch(
      getPlatformParentFieldDataCategoriesAsync({
        platform_field_id: attribute.id,
        platform_Id: platformid,
        page,
        limit: PAGE_SIZE,
        search_string: query,
        selected_value: val?.value || "",
        use_pagination: true,
      })
    );

    let items = res?.payload?.results || res?.payload || [];

    return {
      results: items.map((item: CATEGORY_ATTRIBUTE_FOR_SEARCH) => ({
        value: item.id,
        label: item.api_name,
      })),
      next: res?.payload?.next,
      count: res?.payload?.count,
    };
  };

  useEffect(() => {
    if (!attribute.id) return;

    let cancelled = false;
    const savedValue = val?.value;

    setLoading(true);

    fetchData(1, "")
      .then(({ results, next, count }) => {
        if (cancelled) return;
        setOptions(results);
        setNextPage(next ? 2 : 0);
        setTotalCount(count || 0);

        const existingValue = toOptionalNumericId(savedValue)
        if (existingValue !== null) {
          handleValueChange(attribute.id, existingValue)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [attribute.id, val?.value]);

  const handleSearch = (input: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    searchTimeout.current = setTimeout(async () => {
      setSearch(input);
      setLoading(true);

      const { results, next, count } = await fetchData(1, input);

      setOptions(results);
      setNextPage(next ? 2 : 0);
      setTotalCount(count || 0);

      setLoading(false);
    }, 600);
  };

  const loadMore = useCallback(async () => {
    if (isFetching.current || nextPage === 0) return;
    isFetching.current = true;
    setLoading(true);
    const { results, next } = await fetchData(nextPage, search);
    setOptions((prev) => [...prev, ...results]);
    setNextPage(next ? nextPage + 1 : 0);

    setLoading(false);
    isFetching.current = false;
  }, [nextPage, search]);

  const resolvedValueId =
    selectedValues[attribute.id] ?? toOptionalNumericId(val?.value);

  return (
    <Select
      name="value"
      isMulti={false}
      required={attribute.isRequired}
      isLoading={loading}
      isSearchable
      isClearable={true}
      options={options}
      placeholder={`Select ${attribute.display_name}`}
      value={
        options.find((opt) => opt.value === resolvedValueId) || null
      }
      onInputChange={(val, meta) => {
        if (meta.action === "input-change") handleSearch(val);
      }}
      onMenuScrollToBottom={loadMore}
      onChange={(selected: any) => {
        handleValueChange(attribute.id, selected ? selected.value : null)
      }}
      styles={{
        control: (base) => ({ ...base, minHeight: "32px" }),
        valueContainer: (base) => ({
          ...base,
          maxHeight: "60px",
          overflowY: "auto",
        }),
         menuList: (base) => ({
      ...base,
      maxHeight: "160px",   
      overflowY: "auto",   
    }),

        indicatorSeparator: () => ({ display: "none" }),
      }}
    />
  );
};

export default CategoryAttributeForSelect;
